import { useMemo, useState } from "react";

import OperatorUtilizationKpiCard from "./components/OperatorUtilizationKpiCard";
import OperatorProductionChart from "./components/OperatorProductionChart";
import OperatorPerformanceChart from "./components/OperatorPerformanceChart";
import OperatorRejectReasonsChart from "./components/OperatorRejectReasonsChart";
import OperatorProductionMixChart from "./components/OperatorProductionMixChart";
import OperatorUtilizationTable from "./components/OperatorUtilizationTable";
// import { useLocation } from "react-router-dom";

import "./OperatorUtilizationDashboard.scss";
import { formatDate } from "../../../../../common/Validations";

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits,
  }).format(safeNumber(value));

const sortByProduced = (a, b) =>
  safeNumber(b?.totalProduced) - safeNumber(a?.totalProduced);

const sortByDurationUtilization = (a, b) =>
  safeNumber(b?.durationUtilization) - safeNumber(a?.durationUtilization);

function OperatorUtilizationDashboard({ 
  loading = false,
  data = null,
  filters = {},
}) {
  // const location = useLocation();
  // const { operatorUtilization = null, filters = {} } = location.state || {};
   // Operator data mapped to a generic data variable
  // const data = operatorUtilization;

  const [operatorView, setOperatorView] = useState("top10");

  const dashboardData = useMemo(() => data || {}, [data]);
  const operators = useMemo(
    () =>
      Array.isArray(dashboardData?.operators) ? dashboardData.operators : [],
    [dashboardData],
  );

  const utilizedOperators = useMemo(
    () => operators.filter((operator) => operator?.isUtilized),
    [operators],
  );

  const topProductionOperators = useMemo(() => {
    const sorted = [...utilizedOperators].sort(sortByProduced);
    return operatorView === "all" ? sorted : sorted.slice(0, 10);
  }, [utilizedOperators, operatorView]);

  const topPerformanceOperators = useMemo(() => {
    return [...utilizedOperators]
      .filter((operator) => safeNumber(operator?.durationRecords) > 0)
      .sort(sortByDurationUtilization)
      .slice(0, 10);
  }, [utilizedOperators]);

  const productionMixSeries = useMemo(
    () => [
      safeNumber(dashboardData?.totalOk),
      safeNumber(dashboardData?.totalReject),
      safeNumber(dashboardData?.totalRework),
    ],
    [
      dashboardData?.totalOk,
      dashboardData?.totalReject,
      dashboardData?.totalRework,
    ],
  );

  const kpis = useMemo(
    () => [
      {
        key: "operators",
        eyebrow: "WORKFORCE",
        label: "Operators / Utilized",
        value: `${formatNumber(dashboardData?.totalOperators, 0)} / ${formatNumber(
          dashboardData?.utilizedOperators,
          0,
        )}`,
        detail: `${formatNumber(
          dashboardData?.nonUtilizedOperators,
          0,
        )} currently non-utilized`,
        tone: "indigo",
        icon: "users",
      },
      {
        key: "utilization",
        eyebrow: "UTILIZATION",
        label: "Operator Utilization",
        value: `${formatNumber(dashboardData?.utilizationPercentage)}%`,
        detail: `${formatNumber(
          dashboardData?.utilizedOperators,
          0,
        )} active operators`,
        tone: "cyan",
        icon: "activity",
      },
      {
        key: "production",
        eyebrow: "PRODUCTION",
        label: "Total Produced",
        value: formatNumber(dashboardData?.totalProduced, 0),
        detail: `${formatNumber(dashboardData?.totalJobs, 0)} total jobs`,
        tone: "blue",
        icon: "production",
      },
      {
        key: "quality",
        eyebrow: "QUALITY",
        label: "Overall Quality",
        value: `${formatNumber(dashboardData?.overallQuality)}%`,
        detail: `${formatNumber(dashboardData?.totalOk, 0)} OK quantity`,
        tone: "green",
        icon: "quality",
      },
      {
        key: "duration",
        eyebrow: "TIME",
        label: "Duration Utilization",
        value: `${formatNumber(dashboardData?.overallDurationUtilization)}%`,
        detail: `${formatNumber(
          dashboardData?.totalActualHours,
        )}h actual / ${formatNumber(dashboardData?.totalTargetHours)}h target`,
        tone: "purple",
        icon: "clock",
      },
      {
        key: "reject",
        eyebrow: "REJECTION",
        label: "Rejected Quantity",
        value: formatNumber(dashboardData?.totalReject, 0),
        detail: `${formatNumber(
          dashboardData?.overallRejectionPercentage,
        )}% rejection rate`,
        tone: "red",
        icon: "reject",
      },
    ],
    [dashboardData],
  );

  if (loading) {
    return (
      <section className="operator-utilization-dashboard">
        <div className="operator-utilization-loading">
          <span className="operator-utilization-loading__spinner" />
          <div>
            <strong>Loading operator intelligence...</strong>
            <p>Preparing utilization, production and quality analytics.</p>
          </div>
        </div>
      </section>
    );
  }

  if (!data || !operators.length) {
    return (
      <section className="operator-utilization-dashboard">
        <div className="operator-utilization-empty">
          <span>◎</span>
          <h3>No operator utilization data found</h3>
          <p>Please change the dashboard date filter and refresh the data.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="operator-utilization-dashboard mt-4">
      <div className="operator-utilization-hero">
        <div>
          <span className="operator-utilization-hero__eyebrow">
            LIVE WORKFORCE INTELLIGENCE
          </span>
          <h2>{dashboardData?.title || "Operator / Team Lead Utilization"}</h2>
          <p>
            Real-time workforce productivity, utilization, quality and
            production performance across departments and machines.
          </p>
        </div>

        <div className="operator-utilization-hero__meta">
          <span>
            <i className="operator-live-dot" />
            Live data
          </span>
          <strong>
            {formatDate(filters?.fromDate || "-")}
            {filters?.fromDate !== filters?.toDate
              ? ` → ${formatDate(filters?.toDate || "-")}`
              : ""}
          </strong>
          {/* <strong>
            {dashboardData?.fromDate || "-"} → {dashboardData?.toDate || "-"}
          </strong>
          <small>{dashboardData?.timeZone || "Asia/Kolkata"}</small> */}
        </div>
      </div>

      <div className="operator-utilization-kpi-grid">
        {kpis.map((kpi, index) => (
          <OperatorUtilizationKpiCard
            key={kpi.key}
            {...kpi}
            delay={index * 90}
          />
        ))}
      </div>

      <div className="operator-utilization-chart-grid">
        <div className="operator-utilization-card operator-utilization-card--wide large">
          <div className="operator-utilization-card__header">
            <div>
              <span className="operator-utilization-card__eyebrow">
                PRODUCTION LEADERS
              </span>
              <h3>Top Operator Production</h3>
              <p>
                Highest produced quantity by utilized operators and team leads.
              </p>
            </div>

            <div className="operator-view-switch">
              <button
                type="button"
                className={operatorView === "top10" ? "active" : ""}
                onClick={() => setOperatorView("top10")}
              >
                Top 10
              </button>
              <button
                type="button"
                className={operatorView === "all" ? "active" : ""}
                onClick={() => setOperatorView("all")}
              >
                All
              </button>
            </div>
          </div>

          <OperatorProductionChart
            operators={topProductionOperators}
            // chartKey={`${chartKey}-${operatorView}`}
            loading={loading}
          />
        </div>
      </div>

      <div className="operator-utilization-chart-grid operator-utilization-chart-grid--balanced">
        <div className="operator-utilization-card">
          <div className="operator-utilization-card__header">
            <div>
              <span className="operator-utilization-card__eyebrow">
                PRODUCTION QUALITY
              </span>
              <h3>Production Mix</h3>
              <p>OK versus reject versus rework quantity.</p>
            </div>
          </div>

          <OperatorProductionMixChart
            series={productionMixSeries}
            totalProduced={dashboardData?.totalProduced}
            overallQuality={dashboardData?.overallQuality}
            loading={loading}
            // chartKey={chartKey}
          />
        </div>

        <div className="operator-utilization-card operator-utilization-card--wide">
          <div className="operator-utilization-card__header">
            <div>
              <span className="operator-utilization-card__eyebrow">
                TIME PERFORMANCE
              </span>
              <h3>Operator Duration Performance</h3>
              <p>
                Top operators ranked by duration utilization with quality
                overlay.
              </p>
            </div>
            <span className="operator-utilization-card__badge operator-utilization-card__badge--violet">
              {formatNumber(dashboardData?.overallDurationUtilization)}% overall
            </span>
          </div>

          <OperatorPerformanceChart
            operators={topPerformanceOperators}
            // chartKey={chartKey}
            loading={loading}
          />
        </div>
      </div>

      <div className="operator-utilization-chart-grid operator-utilization-chart-grid--reasons">
        <div className="operator-utilization-card operator-utilization-card--full">
          <div className="operator-utilization-card__header">
            <div>
              <span className="operator-utilization-card__eyebrow">
                QUALITY LOSSES
              </span>
              <h3>Top Rejection Reasons</h3>
              <p>
                Largest rejection quantities to prioritize immediate process
                improvement.
              </p>
            </div>
            <span className="operator-utilization-card__badge operator-utilization-card__badge--danger">
              {formatNumber(dashboardData?.totalReject, 0)} rejected
            </span>
          </div>

          <OperatorRejectReasonsChart
            reasons={dashboardData?.rejectReasons || []}
            // chartKey={chartKey}
            loading={loading}
          />
        </div>
      </div>

      <OperatorUtilizationTable
        operators={operators}
        title="Operator Performance Directory"
      />
    </section>
  );
}

export default OperatorUtilizationDashboard;
